// frontend/src/utils/referencePickerColDef.js
//
// ONE builder for every "reference / foreign-key picker" grid cell — the AG Grid
// Rich Select typeahead used for supplier, user, country, currency, location-type
// and product-category columns, so every editable dropdown cell opens,
// filters-as-you-type, and commits identically.
//
// TWO OPTION SOURCES, ONE INTERFACE:
//
//   SYNC  — pass `options` (an in-memory array). The editor lists ids and
//           formats each to a label; the clipboard carries the LABEL (readable
//           when pasted into Excel; safe because these option labels are
//           effectively unique). The row stores the nested option object, or the
//           raw id with `flat: true` — whichever matches the page's save
//           contract.
//
//   ASYNC — pass `asyncSource` (a server typeahead descriptor, e.g. the ~10K-leaf
//           product-category taxonomy — see categoryPickerColDef.js). The
//           clipboard carries the ID, because the
//           label is deliberately non-unique (a label can't be reversed to one
//           option without risking a silently-wrong pick). Display reads the
//           object off the row; paste rebuilds it from a memo the picker seeds.
//
// The clipboard policy is the ONLY behavioural difference between the two, and
// it's dictated by label uniqueness, not preference. Everything portable (copy,
// paste, fill-handle) works because the value the clipboard moves is a scalar
// resolved back through the column's own valueParser — no grid-level
// processCell* hooks (those have documented grid-wide side effects).

const EM_DASH = '—';

// How every typed-into dropdown list narrows: to the options whose shown label
// CONTAINS the typed text, with the match highlighted. AG Grid's default
// (`searchType: 'fuzzy'`) only re-sorts by edit distance and hides almost
// nothing, so a typed list reads as unfiltered. Every agRichSelectCellEditor
// with a client-side list spreads this; the async (server-side) list filters on
// the server instead.
export const TYPEAHEAD_LIST_PARAMS = Object.freeze({
  allowTyping: true,
  filterList: true,
  searchType: 'matchAny',
  highlightMatch: true,
});

// ── Async option memo ────────────────────────────────────────────────────────
// The clipboard/fill round-trip can only carry a string. For async sources the
// cell copies the option id and rebuilds the full object from this memo on paste.
// Seeded by the editor's paged fetch (via rememberOption in the source's
// valuesPage) and by valueFormatter on every render, so any id a paste can
// produce is already present. A cold miss falls back to an id-only stub: the save
// still writes the correct row; only the label is blank until the row re-renders.
// Reference data (labels stable within a session) → a plain Map with SPA lifetime
// is coherent. Keyed by the option's id value.
const optionMemo = new Map();

export function rememberOption(option, optionId) {
  const id = option?.[optionId];
  if (id != null) optionMemo.set(id, option);
}


/**
 * Build a Rich Select reference-picker column-def fragment. The caller spreads
 * the result and adds page-specific keys (headerName, editable, width, colId,
 * headerTooltip, cellClassRules).
 *
 * @param {object} opts
 * @param {string} opts.field
 *   Column field — also the rowData key. For a SYNC nested column it holds the
 *   option object (`rowData.supplier = { supplier_id, ... }`); for `flat` or the
 *   ASYNC column it holds the raw id / object per that branch's contract.
 * @param {string} opts.optionId
 *   The id field on each option (`'supplier_id'`, `'country_code'`, `'id'`).
 * @param {(option) => string} opts.formatLabel
 *   Option → dropdown/label string.
 * @param {(option) => string} [opts.formatCellLabel]
 *   Option → cell string when it should differ from the dropdown (SYNC only).
 * @param {(option) => string[]} [opts.searchableLabels]
 *   Every string the merchant can type to match an option (SYNC only). The
 *   list filters on what it shows, so each one the label does not already
 *   contain is shown after it — "Poland (PL)". Paste accepts any of them.
 * @param {string} [opts.colId]
 * @param {Array<object>} [opts.options]
 *   SYNC source — the in-memory option list. Mutually exclusive with asyncSource.
 * @param {boolean} [opts.setFkIdField=true]
 *   SYNC nested only — also write `rowData[`${field}_id`]` on commit.
 * @param {boolean} [opts.required=false]
 *   SYNC only — the FK cannot be empty: the list offers no empty choice, and a
 *   clear or an empty paste leaves the cell as it was.
 * @param {boolean} [opts.flat=false]
 *   SYNC only — store the raw id at `rowData[field]` instead of the object.
 * @param {object} [opts.asyncSource]
 *   ASYNC source descriptor: { valuesPage, optionId, formatLabel, placeholder,
 *   valuesPageSize }. Presence of this selects the async branch.
 * @returns {object} Partial column def.
 */
export function referencePickerColDef(opts) {
  if (opts.asyncSource) return asyncReferenceColDef(opts);
  return syncReferenceColDef(opts);
}


// ── SYNC branch ──────────────────────────────────────────────────────────────
// Value shape differs by `flat`; the popup (cellEditorParams) and typed-input
// parser are identical either way — that's what keeps every dropdown behaving the
// same. The clipboard carries the LABEL (valueFormatter output); the parser
// reverses either the label or a raw id back to the id.
function syncReferenceColDef({
  field,
  options,
  optionId,
  formatLabel,
  formatCellLabel,
  searchableLabels,
  setFkIdField = true,
  colId,
  flat = false,
  required = false,
}) {
  if (!field || !optionId || typeof formatLabel !== 'function') {
    throw new Error('referencePickerColDef: field, optionId, and formatLabel are required for a sync source');
  }
  const fkIdField = `${field}_id`;
  const cellFormat = formatCellLabel || formatLabel;
  const labelsFor = searchableLabels || ((opt) => [formatLabel(opt)]);
  const listLabel = (opt) => {
    const label = formatLabel(opt);
    const extra = labelsFor(opt).filter(
      (l) => l && !label.toLowerCase().includes(l.toLowerCase()),
    );
    return extra.length ? `${label} (${extra.join(', ')})` : label;
  };

  const shape = flat
    ? {
        valueGetter: (params) => params.data?.[field] ?? null,
        valueSetter: (params) => {
          const newId = params.newValue ?? null;
          const oldId = params.data?.[field] ?? null;
          if (newId === oldId || (required && newId === null)) return false;
          params.data[field] = newId;
          return true;
        },
        valueFormatter: (params) => {
          const id = params.data?.[field];
          if (id == null) return EM_DASH;
          const opt = options.find((o) => o[optionId] === id);
          return opt ? cellFormat(opt) : EM_DASH;
        },
      }
    : {
        valueGetter: (params) => params.data?.[field]?.[optionId] ?? null,
        valueSetter: (params) => {
          const newId = params.newValue ?? null;
          const oldId = params.data?.[field]?.[optionId] ?? null;
          if (newId === oldId || (required && newId === null)) return false;
          const selected = options.find((o) => o[optionId] === newId) || null;
          params.data[field] = selected;
          if (setFkIdField) params.data[fkIdField] = newId;
          return true;
        },
        valueFormatter: (params) => {
          const opt = params.data?.[field];
          return opt ? cellFormat(opt) : EM_DASH;
        },
      };

  return {
    field,
    ...(colId ? { colId } : {}),
    cellEditor: 'agRichSelectCellEditor',
    cellEditorParams: {
      values: [...(required ? [] : [null]), ...options.map((o) => o[optionId])],
      formatValue: (value) => {
        if (!value) return EM_DASH;
        const opt = options.find((o) => o[optionId] === value);
        return opt ? listLabel(opt) : EM_DASH;
      },
      ...TYPEAHEAD_LIST_PARAMS,
    },
    valueParser: (params) => {
      if (!params.newValue || params.newValue === EM_DASH) return required ? params.oldValue : null;
      if (options.some((o) => o[optionId] === params.newValue)) return params.newValue;
      const text = String(params.newValue).toLowerCase();
      const match = options.find((o) => labelsFor(o).some(
        (label) => label?.toLowerCase() === text,
      ));
      return match ? match[optionId] : params.oldValue;
    },
    ...shape,
  };
}


// ── ASYNC branch ─────────────────────────────────────────────────────────────
// The cell value is the option object; the row-shape is unchanged from the sync
// nested case, but the clipboard carries the ID (labels collide) and paste
// rebuilds the object from the memo. cellDataType:false disables AG Grid's type
// inference (which would coerce the Rich Select commit to a string, losing the
// id). Consumers that need a custom renderer (e.g. a channel gate) read the
// object's label off the row directly — the object is still stored there.
function asyncReferenceColDef({ field, asyncSource }) {
  if (!field || !asyncSource?.valuesPage || !asyncSource.optionId
      || typeof asyncSource.formatLabel !== 'function') {
    throw new Error('referencePickerColDef: asyncSource needs valuesPage, optionId, and formatLabel');
  }
  const { valuesPage, optionId, formatLabel, placeholder, valuesPageSize } = asyncSource;

  return {
    field,
    editable: true,
    cellDataType: false,
    cellEditor: 'agRichSelectCellEditor',
    cellEditorParams: {
      allowTyping: true,
      filterList: true,
      filterListAsync: true,
      searchDebounceDelay: 250,
      valuesPageSize,
      valuesPageLoadThreshold: 8,
      valuePlaceholder: placeholder,
      formatValue: (opt) => (opt ? formatLabel(opt) : ''),
      valuesPage,
    },
    // Default display renderer — reads the object off the row (the formatter is
    // repurposed for the clipboard id). Consumers may override to add a gate.
    cellRenderer: (params) => {
      const opt = params.data?.[field];
      return opt ? formatLabel(opt) : EM_DASH;
    },
    // Clipboard / fill / CSV carry the stable id. Runs for every rendered cell,
    // so it doubles as the memo seed — any option on-screen is copy-able by id.
    valueFormatter: (params) => {
      const opt = params.value;
      if (opt) rememberOption(opt, optionId);
      return opt?.[optionId] ?? '';
    },
    // Reverse of the formatter: a pasted id string → the full object from the
    // memo (id-only stub on a cold miss). An editor commit is already an object.
    valueParser: (params) => {
      const nv = params.newValue;
      if (nv && typeof nv === 'object') return nv;
      if (nv == null || nv === '' || nv === EM_DASH) return null;
      return optionMemo.get(String(nv)) ?? { [optionId]: String(nv) };
    },
    // Change detection by id — a paste yields a new object reference for the same
    // option, which reference equality would misread.
    equals: (a, b) => (a?.[optionId] ?? null) === (b?.[optionId] ?? null),
  };
}
